import requests from "../Locality/Service/httpService";
import { formatDate } from "../../../utils/dateFormat";

/** Admissions CRM API (/api/crm). The school comes from the login token. */
const CrmService = {
  getSettings: () => requests.get("/crm/settings"),
  saveSettings: (body) => requests.put("/crm/settings", body),
  summary: () => requests.get("/crm/enquiries/summary"),
  list: (params) => requests.get("/crm/enquiries", { params }),
  get: (id) => requests.get(`/crm/enquiries/${id}`),
  create: (body) => requests.post("/crm/enquiries", body),
  checkMobile: (mobile) => requests.get("/crm/enquiries/check-mobile", { params: { mobile } }),
  update: (id, body) => requests.put(`/crm/enquiries/${id}`, body),
  remove: (id) => requests.delete(`/crm/enquiries/${id}`),
  addFollowUp: (id, body) => requests.post(`/crm/enquiries/${id}/followups`, body),
  academicYears: () => requests.get("/academicyear/public/enquiry-years"),
  studentDraft: (id) => requests.get(`/crm/enquiries/${id}/student-draft`),
  linkStudent: (id, studentId) => requests.post(`/crm/enquiries/${id}/student`, { studentId }),
  importRows: (rows, allowDuplicates) => requests.post("/crm/import", { rows, allowDuplicates }),
  exportData: (params) => requests.get("/crm/export", { params }),
  dashboard: (month) => requests.get("/crm/dashboard", { params: { month } }),
  searchStudents: (q) => requests.get("/crm/students/search", { params: { q } }),
};

export default CrmService;

/* --------------------------- settings (cached) --------------------------- */

let settingsCache = null;
let settingsFetchedAt = 0;
let settingsPromise = null;
const listeners = new Set();

/**
 * Settings are shared by every CRM screen. The cache is shown at once, and a
 * request older than `maxAgeMs` is refreshed, so changes saved in CRM settings
 * (here or in another tab) reach the enquiry pages without a reload.
 */
export function loadCrmSettings({ maxAgeMs = 0 } = {}) {
  const fresh = settingsCache && Date.now() - settingsFetchedAt < maxAgeMs;
  if (fresh) return Promise.resolve(settingsCache);
  if (!settingsPromise) {
    settingsPromise = CrmService.getSettings()
      .then((res) => setCrmSettingsCache(res))
      .finally(() => {
        settingsPromise = null;
      });
  }
  return settingsPromise;
}

export const getCrmSettingsCache = () => settingsCache;

export function setCrmSettingsCache(next) {
  settingsCache = next;
  settingsFetchedAt = Date.now();
  listeners.forEach((fn) => fn(next));
  return next;
}

export function subscribeCrmSettings(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/* -------------------------------- errors -------------------------------- */

/** { message, errors } from an API error, in plain words. */
export function apiError(err, fallback = "Something went wrong. Please try again.") {
  const data = err?.response?.data || {};
  return {
    message: data.message || (err?.response ? fallback : "Could not reach the server. Check your connection."),
    errors: data.errors || {},
  };
}

/* --------------------------------- dates --------------------------------- */

const DEFAULT_TZ = "Asia/Kolkata";

/** YYYY-MM-DD of a date in the school's time zone. */
export function ymdInTz(value, tz = DEFAULT_TZ) {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  try {
    return new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).format(d);
  } catch (_) {
    return d.toISOString().slice(0, 10);
  }
}

/** HH:mm in the school's time zone ("" when it is midnight, i.e. a date-only value). */
export function timeInTz(value, tz = DEFAULT_TZ) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  try {
    const t = new Intl.DateTimeFormat("en-GB", { timeZone: tz, hour: "2-digit", minute: "2-digit", hour12: false }).format(d);
    return t === "00:00" ? "" : t;
  } catch (_) {
    return "";
  }
}

export const todayYmd = (tz) => ymdInTz(new Date(), tz);

export function addDaysYmd(ymd, days) {
  const [y, m, d] = ymd.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + days));
  return dt.toISOString().slice(0, 10);
}

/** School-format date, e.g. 23-10-2026. */
export const fmtDate = (value, tz) => (value ? formatDate(ymdInTz(value, tz)) : "");

/** 12-hour time, e.g. 2:00 PM. */
export function fmtTime(hhmm) {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":").map(Number);
  const suffix = h >= 12 ? "PM" : "AM";
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, "0")} ${suffix}`;
}

export function fmtDateTime(value, tz) {
  const t = timeInTz(value, tz);
  return `${fmtDate(value, tz)}${t ? `, ${fmtTime(t)}` : ""}`;
}

/** "Today", "Tomorrow", "In 3 days", "2 days overdue" for a follow-up date. */
export function relativeDue(value, tz) {
  if (!value) return "";
  const today = todayYmd(tz);
  const target = ymdInTz(value, tz);
  const diff = Math.round((Date.parse(target) - Date.parse(today)) / 86400000);
  const time = timeInTz(value, tz);
  const at = time ? ` at ${fmtTime(time)}` : "";
  if (diff === 0) return `Today${at}`;
  if (diff === 1) return `Tomorrow${at}`;
  if (diff === -1) return `1 day overdue${time ? ` (${fmtTime(time)})` : ""}`;
  if (diff < 0) return `${-diff} days overdue${time ? ` (${fmtTime(time)})` : ""}`;
  if (diff < 7) return `In ${diff} days${at}`;
  return fmtDateTime(value, tz);
}

/** "2y 5m" from a date of birth, or the age in years given at Quick Add. */
export function childAge(enquiry) {
  if (enquiry?.childDob) {
    const dob = new Date(enquiry.childDob);
    const now = new Date();
    let months = (now.getFullYear() - dob.getFullYear()) * 12 + (now.getMonth() - dob.getMonth());
    if (now.getDate() < dob.getDate()) months -= 1;
    if (months < 0) return "";
    const y = Math.floor(months / 12);
    const m = months % 12;
    return y ? `${y}y ${m}m` : `${m}m`;
  }
  if (enquiry?.childAgeYears != null) return `${enquiry.childAgeYears}y`;
  return "";
}

/* ------------------------------ contact links ------------------------------ */

export const telHref = (phone) => `tel:${String(phone || "").replace(/[^\d+]/g, "")}`;

/** wa.me needs the country code; 10-digit Indian numbers get 91. */
export function whatsappHref(phone, text = "") {
  let digits = String(phone || "").replace(/\D/g, "");
  if (digits.length === 10) digits = `91${digits}`;
  return `https://wa.me/${digits}${text ? `?text=${encodeURIComponent(text)}` : ""}`;
}

/* --------------------------------- status --------------------------------- */

export const STATUS_COLORS = {
  New: "#0b5cc2",
  Contacted: "#c27a00",
  Interested: "#c27a00",
  "Tour Scheduled": "#c27a00",
  "Tour Done": "#c27a00",
  "Application Submitted": "#1f8a5b",
  Admitted: "#1f8a5b",
  Lost: "#b3261e",
};

/** Next sensible status for the follow-up dialog's preselection. */
export function suggestedStatus(current, outcome, type) {
  if (outcome === "Visit scheduled") return "Tour Scheduled";
  if (type === "School Tour/Visit" && ["Tour Scheduled", "Interested", "Contacted", "New"].includes(current)) return "Tour Done";
  if (outcome === "Interested" && ["New", "Contacted"].includes(current)) return "Interested";
  if (outcome === "Connected" && current === "New") return "Contacted";
  return current;
}

/**
 * Student registration page, pre-filled from this enquiry. The id goes in the
 * query string: the app's HashRouter drops history state.
 */
export const studentFormPath = (enquiryId) =>
  `/app/studentregistration/add?fromEnquiry=${encodeURIComponent(String(enquiryId))}`;

/** "2027-2028 (upcoming)" for an enquiry academic-year option. */
export const academicYearLabel = (o) => `${o.name}${o.upcoming ? " (upcoming)" : o.current ? " (current)" : ""}`;

/** Warning when a change would delete the student converted from this enquiry. */
export const studentRemovalWarning = (e) =>
  e?.studentId
    ? ` ${e.childName || "The student"} was registered as a student from this enquiry; that student and all their data will be deleted too.`
    : "";
